import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Mail, MapPin, Coins, Palette, Briefcase, Phone, Instagram, ArrowUp, Send, Heart } from 'lucide-react';
import { mockData } from '../mock';

const Footer = () => {
  const { company } = mockData;
  const [email, setEmail] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(true);

  const footerLinks = [
    {
      title: 'Hizmetlerimiz',
      links: [
        { name: 'Website Geliştirme', path: '/website-hizmetleri', icon: Globe },
        { name: 'Kripto & Blockchain', path: '/kripto-hizmetleri', icon: Coins },
        { name: 'Tasarım Hizmetleri', path: '/tasarim-hizmetleri', icon: Palette },
        { name: 'Portfolyo', path: '/portfolyo', icon: Briefcase }
      ]
    },
    {
      title: 'Şirket',
      links: [
        { name: 'Hakkımızda', path: '/', icon: null },
        { name: 'İletişim', path: '/iletisim', icon: Phone },
        { name: 'Sipariş Süreci', path: '/#process', icon: null },
        { name: 'Referanslar', path: '/portfolyo', icon: null }
      ]
    }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Newsletter logic here
    console.log('Newsletter email:', email);
    setEmail('');
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 dark:from-slate-950 dark:via-purple-950 dark:to-slate-950 text-white overflow-hidden transition-colors duration-300">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500 rounded-full filter blur-3xl animate-float" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-500 rounded-full filter blur-3xl animate-float-slow" />
      </div>

      {/* Wave divider */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-12 sm:h-16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#ffffff" />
        </svg>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-24">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-6 group">
              <img
                src="/viral-logo-2026.png"
                alt="Viral Strateji"
                className="h-12 group-hover:scale-110 transition-transform duration-300"
                style={{
                  filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.4))',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.filter = 'drop-shadow(0 0 12px rgba(139, 92, 246, 0.4))'}
                onMouseLeave={(e) => e.target.style.filter = 'drop-shadow(0 0 6px rgba(139, 92, 246, 0))'}
              />
            </Link>

            <p className="text-gray-300 mb-6 max-w-md leading-relaxed">
              {company.description}
            </p>

            {/* Newsletter */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3 flex items-center space-x-2">
                <Send className="w-5 h-5 text-purple-400" />
                <span>Bültene Katılın</span>
              </h4>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-posta adresiniz"
                  className="flex-1 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all duration-300 text-sm"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-colored text-sm"
                >
                  Gönder
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href={`mailto:${company.contact.email}`}
                className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors duration-200 group"
              >
                <div className="p-2 rounded-lg bg-purple-500/20 group-hover:bg-purple-500/30 transition-colors duration-300">
                  <Mail className="w-4 h-4 text-purple-400" />
                </div>
                <span>{company.contact.email}</span>
              </a>

              <a
                href={`https://${company.contact.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors duration-200 group"
              >
                <div className="p-2 rounded-lg bg-purple-500/20 group-hover:bg-purple-500/30 transition-colors duration-300">
                  <Globe className="w-4 h-4 text-purple-400" />
                </div>
                <span>{company.contact.website}</span>
              </a>

              <a
                href="https://www.instagram.com/viralstrateji/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors duration-200 group"
              >
                <div className="p-2 rounded-lg bg-pink-500/20 group-hover:bg-pink-500/30 transition-colors duration-300">
                  <Instagram className="w-4 h-4 text-pink-400" />
                </div>
                <span>@viralstrateji</span>
              </a>

              <div className="flex items-center space-x-3 text-gray-300">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <MapPin className="w-4 h-4 text-purple-400" />
                </div>
                <span>{company.contact.location}</span>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          {footerLinks.map((section, index) => (
            <div key={index} className="animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
              <h3 className="text-lg font-semibold mb-4 gradient-text-neon">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => {
                  const Icon = link.icon;
                  return (
                    <li key={linkIndex}>
                      <Link
                        to={link.path}
                        className="flex items-center space-x-2 text-gray-300 hover:text-white transition-all duration-200 group"
                      >
                        {Icon && (
                          <Icon className="w-4 h-4 text-purple-400 group-hover:text-pink-400 transition-colors duration-200 group-hover:scale-110 transform" />
                        )}
                        <span className="relative">
                          {link.name}
                          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 group-hover:w-full transition-all duration-300" />
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Payment Methods */}
        <div className="border-t border-white/10 pt-8 mb-8">
          <div className="text-center">
            <h4 className="text-lg font-semibold mb-4 gradient-text">Kabul Edilen Ödeme Yöntemleri</h4>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="glass-dark px-6 py-3 rounded-xl flex items-center space-x-3 hover:bg-purple-500/20 transition-all duration-300 transform hover:scale-105">
                <Coins className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-medium">USDT, BNB, ETH, MATIC</span>
              </div>
              <div className="glass-dark px-6 py-3 rounded-xl flex items-center space-x-3 hover:bg-purple-500/20 transition-all duration-300 transform hover:scale-105">
                <Globe className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-medium">Banka Havalesi / EFT</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm mt-4">
              * Kredi kartı ile ödeme kabul edilmemektedir.
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 pt-8 text-center">
          <p className="text-gray-400 mb-2">
            &copy; 2025 {company.name}. Tüm hakları saklıdır.
          </p>
          <p className="text-gray-500 text-sm flex items-center justify-center space-x-1">
            <span>Dijital dünyada güvenilir çözüm ortağınız</span>
            <Heart className="w-4 h-4 text-pink-500 fill-pink-500 animate-pulse" />
          </p>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full shadow-2xl hover:shadow-neon transform hover:scale-110 transition-all duration-300 z-40 group animate-bounce"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
        </button>
      )}
    </footer>
  );
};

export default Footer;